import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import title from 'Icons/RM_Title.png';
import Logo from 'Icons/RMlogo.png';
import { ReactComponent as WarningIcon } from 'Icons/warning-circle.svg';
import {
  clearUserDataAndLogout,
  getAppMajorVersion,
  getCookie,
  getUserId,
  setCookie,
  showAlertSnackBar,
} from 'utils/Global';
import './Login.scss';
import { ReactComponent as User } from 'Icons/Dashboard/User.svg';
import { ReactComponent as Lock } from 'Icons/Dashboard/lock.svg';
import { ReactComponent as Unlock } from 'Icons/Dashboard/unlock.svg';
import { useHistory } from 'react-router-dom';
import UserGroupService from 'business/services/UserGroupService';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import useModel from 'business/models/useModel';
import RMModal from 'components/modal/RMModal';
import { IStyle } from 'Styles';
import AuthenticationService from 'business/services/AuthenticationService';
import { RadioGroups } from 'components/customComponents/Layers/InputGroups';
import MiscService from 'business/services/MiscService';
import { GetAppInfoData } from 'business/services/GlobalService';
import { AppInfoDTO } from 'business/models/Models';
import { EFeatureTypes, EUserTypes } from 'enums/EFeatureTypes';
import { CacheHelper } from 'utils/CacheHelper';
import { useBackdropLoading } from 'components/modal';
import {
  useGetLicenseValidatorPublicKey,
  useValidateSeatName,
} from 'business/licenseValidator/hooks';
const ResetPasswordBody = () => {
  return (
    <div
      id="login-reset-password-body-root"
      data-testid="login-reset-password-body-root"
      style={styles.root}
    >
      <div
        id="login-reset-password-body-contain"
        data-testid="login-reset-password-body-contain"
      >
        Are you sure you want to reset the password?
      </div>
      <div
        id="login-reset-password-body-icon-row"
        data-testid="login-reset-password-body-icon-row"
        style={styles.iconRow}
      >
        <WarningIcon />
        <div
          id="login-reset-password-body-icon-text"
          data-testid="login-reset-password-body-icon-text"
          style={styles.iconText}
        >
          Please contact the administrator to accept your password reset request.
        </div>
      </div>
    </div>
  );
};

const styles: IStyle = {
  root: {
    flexDirection: 'row',
  },
  header: {
    color: 'white',
  },
  iconRow: {
    padding: '10px',
    alignContent: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: '#FCF3D1',
  },
  iconText: {
    fontSize: '13px',
    color: '#1B1B1B',
  },
};
async function checkIfUserLoggedIn() {
  if (
    (await CacheHelper.get('token')) !== null &&
    localStorage.getItem('user') !== null
  ) {
    window.location.href = '/project';
    return;
  } else {
    await clearUserDataAndLogout(false);
  }
}

function Login() {
  const history = useHistory();
  const userNameRef = useRef(null);
  const [isPasswordSelected, setIsPasswordSelected] = useState(false);
  const [grantAccessToAll, setGrantAccessToAll] = useState(true);
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [appVersion, setAppVersion] = useState(null);
  const [passIcon, setPassIcon] = useState(
    <Lock
      id="login-lock-icon"
      data-testid="login-lock-icon"
      onClick={(e) => onPasswordIcon(false)}
      className="passwordIcon"
    />,
  );
  const { hideBackdrop } = useBackdropLoading();
  const [resetPassword] = useModel(UserGroupService, 'resetPassword');
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showAccessControlInitializeModal, setShowAccessControlInitializeModal] =
    useState(false);
  const [appCopyRights, setAppCopyRights] = useState('');
  const authService = useMemo(() => new AuthenticationService(), []);
  const miscService = useMemo(() => new MiscService(), []);
  const validateSeatName = useValidateSeatName();

  useGetLicenseValidatorPublicKey();

  useEffect(() => {
    window.addEventListener('focus', checkIfUserLoggedIn);

    checkIfUserLoggedIn();

    if (
      userNameRef !== null &&
      userNameRef.current !== null &&
      isPasswordSelected === false
    ) {
      userNameRef.current.focus();
    }
    async function fetchData() {
      if (await getUserId()) {
        history.replace('/');
        return;
      }

      const appDBVersion = await miscService.getAppDBVersion();
      const AuthenticationResp = await authService.isAccessControlEnabled();
      const appInfoData: AppInfoDTO = await GetAppInfoData();
      setAppVersion(getAppMajorVersion(appInfoData.version));
      if (appDBVersion.versionCheckEnabled) {
        if (parseFloat(appDBVersion.dbVersion) != parseFloat(appDBVersion.appVersion)) {
          history.push({
            pathname: '/versionmismatch',
            state: {
              dbVersion: appDBVersion.dbVersion,
              RMVersion: appDBVersion.appVersion,
            },
          });
          return;
        }
      }

      if (!AuthenticationResp) {
        history.push('/accesscontrolerror');
        return;
      }
      if (appInfoData) {
        setAppCopyRights(appInfoData.copyRight);
      }
    }
    fetchData();
    // hide backdrop if it is still showing from previous page
    hideBackdrop();

    return () => window.removeEventListener('focus', checkIfUserLoggedIn);
  }, []);
  function onPasswordIcon(isShowPassword) {
    const passwordInput = document.getElementById(
      'login-input-password',
    ) as HTMLInputElement;

    if (isShowPassword) {
      setPassIcon(
        <Lock
          id="login-lock-icon"
          data-testid="login-lock-icon"
          className="passwordIcon"
          onClick={(e) => onPasswordIcon(false)}
        />,
      );

      if (passwordInput) {
        passwordInput.type = 'password';
      }
    } else {
      setPassIcon(
        <Unlock
          id="login-unlock-icon"
          data-testid="login-unlock-icon"
          className="passwordIcon"
          onClick={(e) => onPasswordIcon(true)}
        />,
      );

      if (passwordInput) {
        passwordInput.type = 'text';
      }
    }
  }
  function openFormulatrixWebsite(e) {
    window.open('http://www.formulatrix.com/', '_blank');
  }
  async function changeUserName(e) {
    setUserName(e.target.value);
  }
  function changePassword(e) {
    if (isPasswordSelected === false) {
      setIsPasswordSelected(true);
    }
    setPassword(e.target.value);
  }

  async function loginUser(e) {
    e.preventDefault();
    await checkIfUserLoggedIn();
    await authenticateUser();
  }

  async function authenticateUser() {
    showAlertSnackBar(EAlertType.INFO, ESnackBarPosition.TOP, 'Logging In...');
    const body = { userName: username, pass: password };

    await authService
      .loginUser(body)
      .then(async (response) => {
        const result = response.data;

        if (response.status === 401) {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Authentication Failed!',
          );
        } else if (result.jwtToken === undefined || result.jwtToken === null) {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Authentication Failed!',
          );
        } else {
          showAlertSnackBar(
            EAlertType.SUCCESS,
            ESnackBarPosition.TOP,
            'Login Successful!',
          );

          await CacheHelper.put('token', result.jwtToken); //, { sameSite: 'Lax' });
          setCookie('upgradeNotified', 'false', { sameSite: 'Lax' });
          setCookie('userType', result.user.userType.id, { sameSite: 'Lax' });
          setCookie('licenseType', result.licenseType, { sameSite: 'Lax' });
          setCookie('licensedFeatures', result.licensedFeatures, { sameSite: 'Lax' });
          //TODO: this is temp. we need to handle this through redux/context
          localStorage.setItem('user', JSON.stringify(result.user));

          if (result.licenseType === 'Seat' || result.hasAnySeatLicense) {
            await validateSeatName();
          }

          if (
            result.user.userType.id === EUserTypes.All &&
            (result.licensedFeatures.some((x) => x === EFeatureTypes.RockMakerWeb) ||
              result.licensedFeatures.some((x) => x === EFeatureTypes.RockMakerLite) ||
              result.licenseType === 'Temporary') &&
            !getCookie(EFeatureTypes.SelectedFeature)
          ) {
            history.push('/featureselection');
          } else {
            const isAccessControlInitializedResp =
              await authService.isAccessControlInitialized();
            if (
              !isAccessControlInitializedResp &&
              result.user.groups.filter((g) => g.id == 1 && g.name == 'Administrators')
                .length > 0
            ) {
              setShowAccessControlInitializeModal(true);
              return;
            }
            setCookie(EFeatureTypes.SelectedFeature, EFeatureTypes.RM);
            // Use window.location.href to trigger a full page reload to avoid any dangling data null reference
            window.location.href = '/project';
          }
          return null;
        }
      })
      .catch((error: any) => {
        console.error('Authentication failed: ', error);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Authentication Failed!',
        );
      });
  }

  const handleResetPasswordClick = () => {
    history.push('/forgotPassword');
  };

  const handlInputOTPClick = () => {
    history.push('/inputOTP');
  };

  const resetPasswordRequest = useCallback(async () => {
    const response = await resetPassword(username);
    showAlertSnackBar(
      response.status === 404 ? EAlertType.ERROR : EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      response.status === 404 ? 'Username not found' : 'Password reset request sent.',
    );
    setShowPasswordResetModal(false);
  }, [username]);

  const accessControlInitializeRequest = async () => {
    await authService.initializeAccessControl(grantAccessToAll);
    setShowAccessControlInitializeModal(false);
    history.push('/project');
    return null;
  };

  const changeAccessControlInitializeOptions = () => {
    setGrantAccessToAll(!grantAccessToAll);
  };

  const AccessControlInitializeBody = () => {
    return (
      <div
        id="login-access-control-initialize-body-root"
        data-testid="login-access-control-initialize-body-root"
        style={styles.root}
      >
        <div
          id="login-access-control-initialize-body-status"
          data-testid="login-access-control-initialize-body-status"
        >
          Access control is turned on.
        </div>
        <div
          id="login-access-control-initialize-body-question"
          data-testid="login-access-control-initialize-body-question"
        >
          Do you want to:
        </div>
        <div
          id="login-access-control-initialize-body-icon-row"
          data-testid="login-access-control-initialize-body-icon-row"
          style={styles.iconRow}
        >
          <RadioGroups
            label1="Grant access to all users"
            label2="Restrict access for all users"
            radioValue={grantAccessToAll}
            onChange={changeAccessControlInitializeOptions}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      id="login-main-container"
      data-testid="login-main-container"
      className="mainContainer"
    >
      <div id="login-header-row" data-testid="login-header-row" className="headerRow">
        <div
          id="login-header-logo"
          data-testid="login-header-logo"
          className="headerLogo"
        >
          <img
            id="login-header-img-logo"
            data-testid="login-header-img-logo"
            src={Logo}
          />
        </div>
        <div
          id="login-header-title"
          data-testid="login-header-title"
          className="headerTitle"
        >
          <img
            id="login-header-title-image"
            data-testid="login-header-title-image"
            className="titleImage"
            src={title}
            alt="RM title"
          />
        </div>
      </div>
      <div
        id="login-inner-container"
        data-testid="login-inner-container"
        className="innerContainer"
      >
        <div
          id="login-login"
          data-testid="login-login"
          className="login flex-y-center flex-column w-[24.33%]"
        >
          <div
            id="login-heading-container"
            data-testid="login-heading-container"
            className="loginHeading flex-y-center flex-column"
          >
            <div id="login-heading" data-testid="login-heading">
              ROCK MAKER
              <span
                id="login-registered-logo"
                data-testid="login-registered-logo"
                className="registeredLogo"
              >
                &#174;
              </span>{' '}
              Crystallization Software
            </div>
            <div
              id="login-sub-heading"
              data-testid="login-sub-heading"
              className="loginSubHeading"
            >
              Log in to your account
            </div>
          </div>

          <div
            id="login-login-form"
            data-testid="login-login-form"
            className="loginForm flex-y-center flex-column w-[85%]"
          >
            <div id="login-user-icon" data-testid="login-user-icon" className="userIcon">
              <User />
            </div>
            <form
              id="login-login-user"
              data-testid="login-login-user"
              className="w-100 mx-auto"
              onSubmit={loginUser}
            >
              <div id="login-username" data-testid="login-username" className="fields">
                <input
                  id="login-input-username"
                  data-testid="login-input-username"
                  className="inputField"
                  ref={userNameRef}
                  placeholder="Username"
                  value={username}
                  onChange={changeUserName}
                  autoFocus
                />
              </div>
              <div id="login-pass-icon" data-testid="login-pass-icon" className="fields">
                <input
                  id="login-input-password"
                  data-testid="login-input-password"
                  className="inputField"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={changePassword}
                />
                {passIcon}
              </div>
              <div
                id="login-submit-container"
                data-testid="login-submit-container"
                className="fields flex-x-center"
              >
                <button
                  id="login-submit-login-button"
                  data-testid="login-submit-login-button"
                  className="loginButton"
                  type="submit"
                >
                  <span
                    id="login-submit-text-button"
                    data-testid="login-submit-text-button"
                    className="buttonText"
                  >
                    Login
                  </span>
                </button>
              </div>
            </form>
            <div
              id="login-advanced-options"
              data-testid="login-advanced-options"
              className="fields flex justify-between"
            >
              <a
                id="login-forget-password-input-otp"
                data-testid="login-forget-password-input-otp"
                className="forgetPassword"
                onClick={handlInputOTPClick}
              >
                I have a code
              </a>
              <a
                id="login-forget-password-reset-password"
                data-testid="login-forget-password-reset-password"
                className="forgetPassword ml-auto"
                onClick={handleResetPasswordClick}
              >
                Forgot password
              </a>
            </div>
          </div>

          <div className="text-[0.75rem] text-[#5D5D5D] mt-[5%] text-center w-[100%] flex">
            For a better experience, use the latest versions of Google Chrome or Microsoft
            Edge
          </div>

          <div
            id="login-bottom-details"
            data-testid="login-bottom-details"
            className="row bottomDetails flex flex-space-between"
          >
            <div
              id="login-bottom-details-app-version"
              data-testid="login-bottom-details-app-version"
              className="col-md-4 bottomDetailsCols"
            >
              Version {appVersion}
            </div>
            <div
              id="login-bottom-details-company-profile"
              data-testid="login-bottom-details-company-profile"
              className="col-md-4 bottomDetailsCols bottomDetailsColsSelected"
              onClick={openFormulatrixWebsite}
            >
              Formulatrix Inc.
            </div>
            <div
              id="login-bottom-details-app-copy-rights"
              data-testid="login-bottom-details-app-copy-rights"
              className="col-md-4 bottomDetailsCols"
            >
              ©{appCopyRights}
            </div>
            <RMModal
              id="login-reset-password"
              data-testid="login-reset-password"
              setShowModal={setShowPasswordResetModal}
              showModal={showPasswordResetModal}
              body={<ResetPasswordBody />}
              heading={'Reset Password'}
              noButtonText={'No, Cancel'}
              onOk={resetPasswordRequest}
            />
            <RMModal
              id="login-access-control"
              data-testid="login-access-control"
              setShowModal={setShowAccessControlInitializeModal}
              showModal={showAccessControlInitializeModal}
              body={<AccessControlInitializeBody />}
              heading={'Access Control'}
              isOkOnly={true}
              okButtonText={'OK'}
              onOk={accessControlInitializeRequest}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
export default Login;
